import { catalogById, catalogId } from './catalog-methods';
import { sha256 } from './sha256';
import { phpCopy, PhpFloat, empty, is_numeric, trim, strval, max, min, compare, usort, floatval, intval, ksort, PhpArray, identical, SORT_STRING, strtolower, strtoupper } from '../../php-compat';
import { array, at, isArray, num, f, list, record, values, put } from './php-shapes';
import { native } from './php-shapes';
import { boxIsWalledRoom, shapeLightFixtures } from '../../../../../ft3d/engine/ft3d-shape-core.js';
import type { PortEnv } from '../../port-contract';
import { ftShapeSightSegmentHit3d } from './ft3d_sight_core';

const EPS = 1e-6;

/** PHP: ft_shape_box_is_navigation_open_ground (includes/functions/janus/encounter/ft3d_shape_core.php:38) */
export function ftShapeBoxIsNavigationOpenGround(box: unknown): boolean {
    const creation = isArray(at(box, 'creation')) ? at(box, 'creation') : [];
    return at(box, 'navigationOpenGround') === true || at(creation, 'enclosure') === 'none'
        || at(creation, 'environment') === 'exterior-open' || at(creation, 'presetId') === 'exterior-open';
}
/** PHP: ft_shape_is_derived_exterior_fallback (includes/functions/janus/encounter/ft3d_shape_core.php:58) */
export function ftShapeIsDerivedExteriorFallback(shape: unknown): boolean {
    const boxes = at(shape, 'boxes');
    if (at(shape, 'derivedExteriorFallback') !== true || !isArray(boxes) || array(boxes).size !== 1
        || !empty(at(shape, 'openings')) || !empty(at(shape, 'sceneWalls'))) return false;
    const box = values(boxes)[0];
    return isArray(box) && !array(box).has('creation') && !array(box).has('interior');
}
/** PHP: ft_shape_is_finite_number (includes/functions/janus/encounter/ft3d_shape_core.php:73) */
export function ftShapeIsFiniteNumber(value: unknown): boolean {
    return (typeof value === 'number' || typeof value === 'bigint' || value instanceof PhpFloat) && Number.isFinite(num(value));
}
/** PHP: ft_shape_containing_boxes (includes/functions/janus/encounter/ft3d_shape_core.php:247) */
export function ftShapeContainingBoxes(shape: unknown, x: unknown, y: unknown): unknown {
    const xx = num(x), yy = num(y);
    return list(values(at(shape, 'boxes')).filter(box => xx >= num(at(box, 'x', 'min')) - EPS && xx <= num(at(box, 'x', 'max')) + EPS
        && yy >= num(at(box, 'y', 'min')) - EPS && yy <= num(at(box, 'y', 'max')) + EPS).map(phpCopy));
}
/** PHP: ft_shape_inside_union (includes/functions/janus/encounter/ft3d_shape_core.php:264) */
export function ftShapeInsideUnion(shape: unknown, x: unknown, y: unknown, env: PortEnv<object>): boolean {
    const boxes = at(shape, 'boxes');
    if (!array(shape).has('boxes')) env.warn('Undefined array key "boxes"', 2);
    if (!isArray(boxes)) {
        const type = boxes === null ? 'null' : boxes instanceof PhpFloat ? 'float' : typeof boxes === 'bigint' ? 'int'
            : typeof boxes === 'number' ? Number.isInteger(boxes) ? 'int' : 'float' : typeof boxes === 'boolean' ? 'bool' : typeof boxes;
        env.warn(`foreach() argument must be of type array|object, ${type} given`, 2);
        return false;
    }
    return array(ftShapeContainingBoxes(shape, x, y)).size > 0;
}
/** PHP: ft_shape_strictly_inside_any_box (includes/functions/janus/encounter/ft3d_shape_core.php:282) */
export function ftShapeStrictlyInsideAnyBox(shape: unknown, x: unknown, y: unknown, z: unknown): boolean {
    return values(at(shape, 'boxes')).some(box => !ftShapeBoxIsNavigationOpenGround(box)
        && num(x) > num(at(box, 'x', 'min')) + EPS && num(x) < num(at(box, 'x', 'max')) - EPS
        && num(y) > num(at(box, 'y', 'min')) + EPS && num(y) < num(at(box, 'y', 'max')) - EPS
        && num(z) >= num(at(box, 'floor')) - EPS && num(z) <= num(at(box, 'ceiling')) + EPS);
}
/** PHP: ft_shape_ceiling_at (includes/functions/janus/encounter/ft3d_shape_core.php:300) */
export function ftShapeCeilingAt(shape: unknown, x: unknown, y: unknown): unknown {
    let boxes = values(ftShapeContainingBoxes(shape, x, y));
    if (!boxes.length) return null;
    const structural = boxes.filter(box => !ftShapeBoxIsNavigationOpenGround(box));
    if (structural.length) boxes = structural;
    let best: number | null = null;
    for (const box of boxes) { const c = num(at(box, 'ceiling')); if (best === null || c > best) best = c; }
    return best === null ? null : f(best);
}
/** PHP: ft_shape_opening_admits (includes/functions/janus/encounter/ft3d_shape_core.php:341) */
export function ftShapeOpeningAdmits(opening: unknown, axis: string, plane: unknown, span: unknown, z: unknown, mode: string): boolean {
    if (at(opening, 'plane', 'axis') !== axis || Math.abs(num(at(opening, 'plane', 'at')) - num(plane)) > EPS) return false;
    if (num(span) < num(at(opening, 'span', 'min')) - EPS || num(span) > num(at(opening, 'span', 'max')) + EPS) return false;
    if (num(z) < num(at(opening, 'z', 'min')) - EPS || num(z) > num(at(opening, 'z', 'max')) + EPS) return false;
    return mode === 'move' ? (at(opening, 'passable') ?? true) !== false
        : (at(opening, 'transparent') ?? true) !== false && (at(opening, 'coveringState', 'blocksSight') ?? false) !== true;
}
/** PHP: ft_shape_scene_wall_faces (includes/functions/janus/encounter/ft3d_shape_core.php:544) */
export function ftShapeSceneWallFaces(shape: unknown): unknown {
    const faces = [];
    for (const wall of values(at(shape, 'sceneWalls'))) {
        if (!isArray(wall) || !['x', 'y'].includes(strval(at(wall, 'axis')))) continue;
        const axis = strval(at(wall, 'axis')), other = axis === 'x' ? 'y' : 'x';
        const plane = num(at(wall, 'at') ?? 0), half = Math.max(.05, num(at(wall, 'thicknessFt') ?? .5)) / 2;
        const low = num(at(wall, 'span', 'min') ?? 0), high = num(at(wall, 'span', 'max') ?? 0);
        const floor = num(at(wall, 'z', 'min') ?? 0), ceiling = num(at(wall, 'z', 'max') ?? 0);
        if (!(high > low) || !(ceiling > floor)) continue;
        const openings = list(values(at(wall, 'openings')).filter(isArray).map(o => record({ min: floatval(at(o, 'span', 'min') ?? 0), max: floatval(at(o, 'span', 'max') ?? 0),
            zmin: floatval(at(o, 'z', 'min') ?? floor), zmax: floatval(at(o, 'z', 'max') ?? ceiling) })));
        for (const p of [plane - half, plane + half]) faces.push(record({ axis, at: f(p), min: f(low), max: f(high), floor: f(floor), ceiling: f(ceiling), openings: phpCopy(openings) }));
        for (const cap of [low, high]) faces.push(record({ axis: other, at: f(cap), min: f(plane - half), max: f(plane + half), floor: f(floor), ceiling: f(ceiling), openings: list([]) }));
    }
    return list(faces);
}
/** PHP: ft_shape_carry_scene_walls (includes/functions/janus/encounter/ft3d_shape_core.php:584) */
export function ftShapeCarrySceneWalls(shape: unknown, stored: unknown): unknown {
    const copy = phpCopy(array(shape));
    if (!copy.has('sceneWalls') && isArray(at(stored, 'sceneWalls')) && array(at(stored, 'sceneWalls')).size) copy.set('sceneWalls', phpCopy(at(stored, 'sceneWalls')));
    for (const [i, wall] of isArray(at(copy, 'sceneWalls')) ? array(at(copy, 'sceneWalls')) : []) {
        if (!isArray(wall) || !isArray(at(wall, 'openings'))) continue;
        const revised = phpCopy(array(wall));
        revised.set('openings', list(values(at(wall, 'openings')).filter(o => !isArray(o) || at(o, 'creation', 'wallBreach') === null).map(phpCopy)));
        array(at(copy, 'sceneWalls')).set(i, revised);
    }
    return copy;
}
/** PHP: ft_shape_scene_wall_face_admits (includes/functions/janus/encounter/ft3d_shape_core.php:600) */
export function ftShapeSceneWallFaceAdmits(face: unknown, span: unknown, z: unknown): boolean {
    return values(at(face, 'openings')).some(o => num(span) > num(at(o, 'min')) + EPS && num(span) < num(at(o, 'max')) - EPS
        && num(z) >= num(at(o, 'zmin')) - EPS && num(z) <= num(at(o, 'zmax')) + EPS);
}
/** PHP: ft_shape_segment_interval_for_axis (includes/functions/janus/encounter/ft3d_shape_core.php:709) */
export function ftShapeSegmentIntervalForAxis(origin: unknown, delta: unknown, low: unknown, high: unknown): unknown {
    const o = num(origin), d = num(delta), lo = num(low), hi = num(high);
    if (Math.abs(d) < EPS) return o >= lo - EPS && o <= hi + EPS ? list([f(-Infinity), f(Infinity)]) : null;
    const a = f((lo - o) / d), b = f((hi - o) / d); return list([min(a, b), max(a, b)]);
}
/** PHP: ft_shape_intersect_intervals (includes/functions/janus/encounter/ft3d_shape_core.php:721) */
export function ftShapeIntersectIntervals(intervals: unknown): unknown {
    let enter: unknown = f(-Infinity), exit: unknown = f(Infinity);
    for (const interval of values(intervals)) {
        if (interval === null) return null;
        enter = max(enter, at(interval, 0)); exit = min(exit, at(interval, 1));
    }
    return num(exit) >= num(enter) - EPS ? list([enter, exit]) : null;
}
/** PHP: ft_structure_state (includes/functions/janus/encounter/ft3d_shape_core.php:1074) */
export function ftStructureState(currentValue: unknown, maxValue: unknown, cleared = false): string {
    const maximum = is_numeric(maxValue) ? num(maxValue) : 0.0, current = is_numeric(currentValue) ? num(currentValue) : 0.0;
    if (maximum <= 0) return 'intact';
    if (current <= 0) return 'destroyed';
    if (current <= maximum * .5 + EPS) return cleared ? 'cleared' : 'damaged';
    return 'intact';
}
/** PHP: ft_structure_passable_at (includes/functions/janus/encounter/ft3d_shape_core.php:1092) */
export function ftStructurePassableAt(kind: string, state: string): string | null {
    if (kind === 'window' && state === 'damaged') return 'cost';
    if (kind === 'window' && (state === 'cleared' || state === 'destroyed') || (kind === 'wall' || kind === 'door') && state === 'destroyed') return 'free';
    return null;
}
/** PHP: ft_structure_opening_effect (includes/functions/janus/encounter/ft3d_shape_core.php:1113) */
export function ftStructureOpeningEffect(kind: string, state: string, open: boolean, transparentWhenShut: boolean): unknown {
    if (state === 'intact') return record({ passable: open, transparent: open || transparentWhenShut, crossing: open ? 'free' : null });
    const crossing = ftStructurePassableAt(kind, state);
    return record({ passable: crossing !== null, transparent: state === 'destroyed' || crossing !== null || transparentWhenShut, crossing });
}
/** PHP: ft_shape_is_runtime_door (includes/functions/janus/encounter/ft3d_shape_core.php:1132) */
export function ftShapeIsRuntimeDoor(opening: unknown): boolean {
    return strval(at(opening, 'creation', 'connectionIntent', 'kind') ?? '') === 'door-intent' && trim(strval(at(opening, 'id') ?? '')) !== '';
}
/** PHP: ft_shape_runtime_structure_kind (includes/functions/janus/encounter/ft3d_shape_core.php:1463) */
export function ftShapeRuntimeStructureKind(opening: unknown): string {
    if (trim(strval(at(opening, 'id') ?? '')) === '') return '';
    const kind = strval(at(opening, 'creation', 'connectionIntent', 'kind') ?? ''); return kind === 'door-intent' ? 'door' : kind === 'window-intent' ? 'window' : '';
}
/** PHP: ft_shape_opening_transparent_when_shut (includes/functions/janus/encounter/ft3d_shape_core.php:1482) */
export function ftShapeOpeningTransparentWhenShut(opening: unknown): boolean {
    return at(opening, 'creation', 'connectionIntent', 'transparentWhenShut') === true;
}
/** PHP: ft_shape_carry_water_bodies (includes/functions/janus/encounter/ft3d_shape_core.php:1746) */
export function ftShapeCarryWaterBodies(shape: unknown, stored: unknown): unknown {
    const copy = phpCopy(array(shape));
    if (isArray(stored) && array(stored).has('waterBodies')) copy.set('waterBodies', phpCopy(array(stored).get('waterBodies')));
    else copy.delete('waterBodies');
    return copy;
}
/** PHP: ft_shape_strip_concealed_openings (includes/functions/janus/encounter/ft3d_shape_core.php:1761) */
export function ftShapeStripConcealedOpenings(shape: unknown, concealed: unknown): unknown {
    const copy = phpCopy(array(shape));
    if (!array(concealed).size || !isArray(at(copy, 'openings'))) return copy;
    copy.set('openings', list(values(at(copy, 'openings')).filter(o => !isArray(o) || at(concealed, trim(strval(at(o, 'id') ?? ''))) === null).map(phpCopy)));
    return copy;
}
/** PHP: ft_aperture_latch_sides (includes/functions/janus/encounter/ft3d_shape_core.php:1969) */
export function ftApertureLatchSides(opening: unknown): unknown {
    const kind = ftShapeRuntimeStructureKind(opening), barred = at(opening, 'creation', 'connectionIntent', 'barredFrom');
    if (typeof barred === 'string' && barred !== '') return list([barred]);
    return list(kind === 'door' ? ['inside', 'outside'] : kind === 'window' ? ['inside'] : []);
}
/** PHP: ft_aperture_interior_room_id (includes/functions/janus/encounter/ft3d_shape_core.php:1983) */
export function ftApertureInteriorRoomId(opening: unknown): string {
    const rooms = at(opening, 'creation', 'rooms'); return isArray(rooms) && array(rooms).size === 1 ? strval(at(rooms, 0)) : '';
}
/** PHP: ft_light_rank (includes/functions/janus/encounter/ft3d_shape_core.php:2096) */
export function ftLightRank(light: string): number { return light === '0CC8A592-CF4C-7BE4-DD0C-17A41D690A05' ? 2 : light === '61D1498F-9175-6906-4B26-8356C28B28C1' ? 1 : 0; }
/** PHP: ft_shape_box_is_walled_room (includes/functions/janus/encounter/ft3d_shape_core.php:2200) */
export function ftShapeBoxIsWalledRoom(box: unknown): boolean {
    return boxIsWalledRoom(native(box) as Parameters<typeof boxIsWalledRoom>[0]);
}
/** PHP: ft_shape_opening_facing_from_geometry (includes/functions/janus/encounter/ft3d_shape_core.php:2211) */
export function ftShapeOpeningFacingFromGeometry(boxes: unknown, opening: unknown): string {
    const axis = at(opening, 'plane', 'axis');
    if (axis !== 'x' && axis !== 'y' || at(opening, 'span', 'min') === null || at(opening, 'z', 'min') === null) return 'exterior';
    const rooms = values(at(opening, 'creation', 'rooms')).map(strval);
    if (rooms.length >= 2) return 'interior';
    const plane = num(at(opening, 'plane', 'at')), centre = (num(at(opening, 'span', 'min')) + num(at(opening, 'span', 'max'))) / 2;
    const along = axis === 'x' ? 'y' : 'x', zMin = num(at(opening, 'z', 'min')), zMax = num(at(opening, 'z', 'max')), sides = new Set<number>();
    for (const box of values(boxes)) {
        if (!isArray(box) || at(box, axis, 'min') === null || at(box, along, 'min') === null || !ftShapeBoxIsWalledRoom(box)) continue;
        if (!(centre > num(at(box, along, 'min')) + EPS && centre < num(at(box, along, 'max')) - EPS)) continue;
        if (!(num(at(box, 'floor')) < zMax - EPS && num(at(box, 'ceiling')) > zMin + EPS)) continue;
        if (Math.abs(num(at(box, axis, 'min')) - plane) < EPS) sides.add(1);
        if (Math.abs(num(at(box, axis, 'max')) - plane) < EPS) sides.add(-1);
    }
    if (rooms.length === 1) for (const box of values(boxes)) {
        if (!isArray(box) || strval(at(box, 'id') ?? '') !== rooms[0] || at(box, axis, 'min') === null) continue;
        const ownerSide = Math.abs(num(at(box, axis, 'min')) - plane) < EPS ? 1 : Math.abs(num(at(box, axis, 'max')) - plane) < EPS ? -1 : 0;
        if (ownerSide !== 0) return sides.has(-ownerSide) ? 'interior' : 'exterior';
        break;
    }
    return sides.has(1) && sides.has(-1) ? 'interior' : 'exterior';
}
/** PHP: ft_shape_opening_facing (includes/functions/janus/encounter/ft3d_shape_core.php:2242) */
export function ftShapeOpeningFacing(shape: unknown, opening: unknown): string {
    const stamped = at(opening, 'creation', 'facing'); return stamped === 'exterior' || stamped === 'interior' ? stamped : ftShapeOpeningFacingFromGeometry(at(shape, 'boxes') ?? [], opening);
}
/** PHP: ft_shape_opening_on_box_face (includes/functions/janus/encounter/ft3d_shape_core.php:2250) */
export function ftShapeOpeningOnBoxFace(opening: unknown, box: unknown): boolean {
    const axis = at(opening, 'plane', 'axis');
    if (axis !== 'x' && axis !== 'y' || at(box, axis, 'min') === null || at(opening, 'span', 'min') === null || at(opening, 'z', 'min') === null) return false;
    const along = axis === 'x' ? 'y' : 'x', plane = num(at(opening, 'plane', 'at')), centre = (num(at(opening, 'span', 'min')) + num(at(opening, 'span', 'max'))) / 2;
    return (Math.abs(num(at(box, axis, 'min')) - plane) < EPS || Math.abs(num(at(box, axis, 'max')) - plane) < EPS)
        && centre > num(at(box, along, 'min')) + EPS && centre < num(at(box, along, 'max')) - EPS
        && num(at(opening, 'z', 'min')) < num(at(box, 'ceiling')) - EPS && num(at(opening, 'z', 'max')) > num(at(box, 'floor')) + EPS;
}
/** PHP: ft_shape_aperture_far_side_point (includes/functions/janus/encounter/ft3d_shape_core.php:2263) */
export function ftShapeApertureFarSidePoint(opening: unknown, box: unknown): unknown {
    const axis = strval(at(opening, 'plane', 'axis')), plane = num(at(opening, 'plane', 'at'));
    const out = Math.abs(num(at(box, axis, 'max')) - plane) < EPS ? 1 : Math.abs(num(at(box, axis, 'min')) - plane) < EPS ? -1 : 0;
    if (out === 0) return null;
    const centre = (num(at(opening, 'span', 'min')) + num(at(opening, 'span', 'max'))) / 2;
    return axis === 'x' ? record({ x: f(plane + out * .5), y: f(centre) }) : record({ x: f(centre), y: f(plane + out * .5) });
}
/** PHP: ft_shape_opening_passes_light (includes/functions/janus/encounter/ft3d_shape_core.php:2278) */
export function ftShapeOpeningPassesLight(opening: unknown): boolean {
    if (at(opening, 'concealed') === true || at(opening, 'coveringState', 'blocksLight') === true) return false;
    return array(opening).has('passesLight') ? at(opening, 'passesLight') === true : (at(opening, 'transparent') ?? true) !== false;
}
/** PHP: ft_shape_point_within_aperture_reach (includes/functions/janus/encounter/ft3d_shape_core.php:2290) */
export function ftShapePointWithinApertureReach(opening: unknown, x: unknown, y: unknown): boolean {
    const axis = at(opening, 'plane', 'axis'); if (axis !== 'x' && axis !== 'y') return false;
    const plane = num(at(opening, 'plane', 'at')), low = num(at(opening, 'span', 'min')), high = num(at(opening, 'span', 'max'));
    const through = axis === 'x' ? Math.abs(num(x) - plane) : Math.abs(num(y) - plane), along = axis === 'x' ? num(y) : num(x);
    return through <= 10 + EPS && along >= low - 10 - EPS && along <= high + 10 + EPS;
}
/** PHP: ft_sight_ray_range (includes/functions/janus/encounter/ft3d_shape_core.php:2673) */
export function ftSightRayRange(c0: unknown, u: unknown, low: unknown, high: unknown, length: unknown): unknown {
    let a = 0.0, b = num(length);
    if (Math.abs(num(u)) < 1e-12) { if (num(c0) < num(low) || num(c0) > num(high)) return null; }
    else { a = (num(low) - num(c0)) / num(u); b = (num(high) - num(c0)) / num(u); if (a > b) [a, b] = [b, a]; }
    a = num(max(f(a), f(0))); b = num(min(f(b), floatval(length))); return b > a ? list([f(a), f(b)]) : null;
}
/** PHP: ft_sight_intersect (includes/functions/janus/encounter/ft3d_shape_core.php:2693) */
export function ftSightIntersect(p: unknown, q: unknown): unknown {
    if (p === null || q === null) return null;
    const a = max(at(p, 0), at(q, 0)), b = min(at(p, 1), at(q, 1)); return compare(b, a) > 0 ? list([a, b]) : null;
}
/** PHP: ft_sight_merge (includes/functions/janus/encounter/ft3d_shape_core.php:2703) */
export function ftSightMerge(intervals: unknown): unknown {
    const sorted = usort(array(intervals), (p, q) => compare(at(p, 0), at(q, 0)) || compare(at(p, 1), at(q, 1)));
    const out: unknown[] = [];
    for (const iv of values(sorted)) {
        const previous = out[out.length - 1];
        if (out.length && compare(at(iv, 0), at(previous, 1)) <= 0) array(previous).set(1, max(at(previous, 1), at(iv, 1)));
        else out.push(list([phpCopy(at(iv, 0)), phpCopy(at(iv, 1))]));
    }
    return list(out);
}
/** PHP: ft_aoe_arc_launch_angle_deg (includes/functions/janus/encounter/ft3d_shape_core.php:2940) */
export function ftAoeArcLaunchAngleDeg(distance: unknown, range: unknown): unknown {
    if (num(distance) <= 0.0) return f(90);
    if (range === null || !Number.isFinite(num(range)) || num(range) <= 0.0) return f(45);
    const ratio = Math.min(1.0, Math.max(0.0, num(distance) / num(range))); return f(90 - (90 - 45) * ratio);
}
/** PHP: ft_aoe_arc_apex_height_ft (includes/functions/janus/encounter/ft3d_shape_core.php:2951) */
export function ftAoeArcApexHeightFt(distance: unknown, range: unknown): unknown {
    if (num(distance) <= 0.0) return f(0);
    const angle = num(ftAoeArcLaunchAngleDeg(distance, range));
    const tangent = angle >= 90 - 1e-9 ? Infinity : Math.tan(angle * (Math.PI / 180));
    return f(Math.min(60, num(distance) * tangent / 4.0));
}
/** PHP: ft_aoe_arc_samples (includes/functions/janus/encounter/ft3d_shape_core.php:2961) */
export function ftAoeArcSamples(emitter: unknown, landing: unknown, range: unknown): unknown {
    const distance = Math.hypot(num(at(landing, 'x')) - num(at(emitter, 'x')), num(at(landing, 'y')) - num(at(emitter, 'y')));
    const apex = num(ftAoeArcApexHeightFt(distance, range)), out = [];
    for (let i = 0; i <= 12; i++) {
        const t = i / 12;
        out.push(record({ x: f(num(at(emitter, 'x')) + (num(at(landing, 'x')) - num(at(emitter, 'x'))) * t),
            y: f(num(at(emitter, 'y')) + (num(at(landing, 'y')) - num(at(emitter, 'y'))) * t),
            z: f(num(at(emitter, 'z')) + (num(at(landing, 'z')) - num(at(emitter, 'z'))) * t + 4.0 * apex * t * (1.0 - t)) }));
    }
    return list(out);
}

interface ShapeReaders {
    inventory_field(row: unknown, field: string): unknown;
    item_field(row: unknown, field: string): unknown;
    layer_value(row: unknown, field: string): unknown;
    check_uuid(id: unknown): boolean;
    get_inventory_arr(id: string): unknown;
    creature_statuses(row: unknown, direction: string): unknown;
    ft_shape_private_door_inventory_ids(ids: unknown): unknown;
    ft_shape_opening_vitality_rows(shape: unknown, encounter: unknown): unknown;
    ft_inventory_lock_bindings_for_host(id: string): unknown;
    ft_encounter_id_is_depth_authored(id: unknown): boolean;
    get_object_height_feet(row: unknown): unknown;
    ft_shape_floor_at(shape: unknown, x: unknown, y: unknown): unknown;
    ft_encounter_shape(row: unknown): unknown;
    encounter_field(row: unknown, field: string): unknown;
    ft_shape_object_center(row: unknown): unknown;
    ft_shape_ray_z(shape: unknown, row: unknown, x: unknown, y: unknown): unknown;
    ft_shape_box_is_roofed(box: unknown): boolean;
    ft_shape_stair_contour(connection: unknown, box: unknown): unknown;
}
type ShapeEnv = PortEnv<ShapeReaders>;

/** Common face traversal; the two PHP entry points retain their own length expression. */
function segmentFaces(shape: unknown, sx: number, sy: number, sz: number, tx: number, ty: number, tz: number,
    mode: string, contain: boolean, threeD: boolean, env: PortEnv<object>): unknown {
    const dx = tx - sx, dy = ty - sy, dz = tz - sz;
    const length = threeD ? Math.hypot(dx, dy) : Math.sqrt(dx * dx + dy * dy);
    let best: unknown = null;
    const hit = (axis: string, plane: number, low: number, high: number, floor: number, ceiling: number, face?: unknown): void => {
        if (!threeD && (sz < floor - EPS || sz > ceiling + EPS)) return;
        const origin = axis === 'x' ? sx : sy, delta = axis === 'x' ? dx : dy;
        if (Math.abs(delta) < EPS) return;
        const t = (plane - origin) / delta;
        if (t <= EPS || t >= 1 - EPS) return;
        const x = sx + t * dx, y = sy + t * dy, z = threeD ? sz + t * dz : sz;
        const span = axis === 'x' ? y : x;
        if (span < low - EPS || span > high + EPS || threeD && (z < floor - EPS || z > ceiling + EPS)
            || best !== null && t >= num(at(best, 't'))) return;
        const result = (): unknown => record(threeD
            ? { t: f(t), x: f(x), y: f(y), z: f(z), axis, at: f(plane) }
            : { t: f(t), x: f(x), y: f(y), axis, at: f(plane) });
        if (face !== undefined) {
            if (!ftShapeSceneWallFaceAdmits(face, span, z)) best = result();
            return;
        }
        if (ftShapeStrictlyInsideAnyBox(shape, x, y, z)) return;
        if (values(at(shape, 'openings')).some(o => ftShapeOpeningAdmits(o, axis, plane, span, z, mode))) {
            if (mode === 'move' || contain) {
                const ahead = Math.min(1.0, t + .01 / Math.max(.01, length));
                if (!ftShapeInsideUnion(shape, sx + ahead * dx, sy + ahead * dy, env)) best = result();
            }
            return;
        }
        best = result();
    };
    for (const box of values(at(shape, 'boxes'))) {
        if (ftShapeBoxIsNavigationOpenGround(box)) continue;
        const x0 = num(at(box, 'x', 'min')), x1 = num(at(box, 'x', 'max'));
        const y0 = num(at(box, 'y', 'min')), y1 = num(at(box, 'y', 'max'));
        const floor = num(at(box, 'floor')), ceiling = num(at(box, 'ceiling'));
        hit('x', x0, y0, y1, floor, ceiling); hit('x', x1, y0, y1, floor, ceiling);
        hit('y', y0, x0, x1, floor, ceiling); hit('y', y1, x0, x1, floor, ceiling);
    }
    for (const face of values(ftShapeSceneWallFaces(shape))) hit(strval(at(face, 'axis')), num(at(face, 'at')),
        num(at(face, 'min')), num(at(face, 'max')), num(at(face, 'floor')), num(at(face, 'ceiling')), face);
    return best;
}
/** PHP: ft_shape_segment_hit (includes/functions/janus/encounter/ft3d_shape_core.php:372) */
export function ftShapeSegmentHit(shape: unknown, x1: unknown, y1: unknown, x2: unknown, y2: unknown,
    z: unknown = 0.5, mode = 'sight', contain = false, env: PortEnv<object>): unknown {
    return segmentFaces(shape, num(x1), num(y1), num(z), num(x2), num(y2), num(z), mode, contain, false, env);
}
/** PHP: ft_shape_segment_hit_3d (includes/functions/janus/encounter/ft3d_shape_core.php:611) */
export function ftShapeSegmentHit3d(shape: unknown, source: unknown, target: unknown,
    mode = 'sight', contain = false, env: PortEnv<object>): unknown {
    return segmentFaces(shape, num(at(source, 'x')), num(at(source, 'y')), num(at(source, 'z')),
        num(at(target, 'x')), num(at(target, 'y')), num(at(target, 'z')), mode, contain, true, env);
}
/** PHP: ft_shape_clip_radial_aoe (includes/functions/janus/encounter/ft3d_shape_core.php:491) */
export function ftShapeClipRadialAoe(shape: unknown, cx: unknown, cy: unknown, radius: unknown,
    z: unknown = 1.0, rayCount: unknown = 128, env: PortEnv<object>): unknown {
    const x = num(cx), y = num(cy), r = num(radius), count = Number(intval(rayCount)), fullArea = Math.PI * r * r;
    if (!ftShapeInsideUnion(shape, x, y, env)) return record({ points: list([]), area: f(0), fullArea: f(fullArea) });
    const points: unknown[] = [];
    for (let i = 0; i < count; i++) {
        const angle = (i / count) * Math.PI * 2, tx = x + r * Math.cos(angle), ty = y + r * Math.sin(angle);
        const hit = ftShapeSegmentHit(shape, x, y, tx, ty, z, 'sight', true, env);
        const reach = hit !== null ? Math.max(0.0, num(at(hit, 't')) - 1e-4) : 1.0;
        points.push(record({ x: f(x + (tx - x) * reach), y: f(y + (ty - y) * reach) }));
    }
    let area = 0.0;
    for (let i = 0; i < points.length; i++) {
        const a = points[i], b = points[(i + 1) % points.length];
        area += num(at(a, 'x')) * num(at(b, 'y')) - num(at(b, 'x')) * num(at(a, 'y'));
    }
    return record({ points: list(points), area: f(Math.abs(area) / 2), fullArea: f(fullArea) });
}
/** PHP: ft_shape_radial_aoe_contains (includes/functions/janus/encounter/ft3d_shape_core.php:524) */
export function ftShapeRadialAoeContains(shape: unknown, cx: unknown, cy: unknown, radius: unknown,
    x: unknown, y: unknown, z: unknown = 1.0, env: PortEnv<object>): boolean {
    const dx = num(x) - num(cx), dy = num(y) - num(cy);
    if (Math.sqrt(dx * dx + dy * dy) > num(radius) + EPS || !ftShapeInsideUnion(shape, x, y, env)) return false;
    return ftShapeSegmentHit(shape, cx, cy, x, y, z, 'sight', true, env) === null;
}
/** PHP: ft_shape_scene_wall_face_point (includes/functions/janus/encounter/ft3d_shape_core.php:1273) */
export function ftShapeSceneWallFacePoint(shape: unknown, wallId: string, x: unknown, y: unknown, runSpan: unknown = null): unknown {
    for (const wall of values(at(shape, 'sceneWalls'))) {
        if (!isArray(wall) || at(wall, 'id') !== wallId || !['x', 'y'].includes(strval(at(wall, 'axis')))) continue;
        const plane = num(at(wall, 'at')), xx = num(x), yy = num(y), axis = at(wall, 'axis');
        const offset = (Math.max(.05, num(at(wall, 'thicknessFt') ?? .5)) / 2 + .05) * ((axis === 'x' ? xx : yy) >= plane ? 1.0 : -1.0);
        const low = Math.max(num(at(wall, 'span', 'min')), num(at(runSpan, 'min') ?? f(-Infinity)));
        const high = Math.min(num(at(wall, 'span', 'max')), num(at(runSpan, 'max') ?? f(Infinity)));
        if (!(high >= low)) return null;
        const along = Math.max(low, Math.min(high, axis === 'x' ? yy : xx));
        return record(axis === 'x' ? { x: f(plane + offset), y: f(along) } : { x: f(along), y: f(plane + offset) });
    }
    return null;
}
/** PHP: ft_shape_door_identity_see_through (includes/functions/janus/encounter/ft3d_shape_core.php:1514) */
export function ftShapeDoorIdentitySeeThrough(opening: unknown, inventory: unknown, env: ShapeEnv): boolean {
    if (ftShapeRuntimeStructureKind(opening) !== 'door') return false;
    let itemId = '';
    if (isArray(inventory) && !empty(inventory)) {
        let raw = at(inventory, 'inventory_item_id') ?? at(inventory, 'inventory', 'inventory_item_id')
            ?? env.deps.inventory_field(inventory, 'inventory_item_id') ?? at(inventory, 'item_id');
        if (isArray(raw)) raw = at(raw, 0);
        itemId = raw !== null && !isArray(raw) ? trim(strval(raw)) : '';
    }
    if (itemId === '') itemId = strval(at(opening, 'creation', 'connectionIntent', 'doorItemId') ?? '');
    return ['90EDF803-5302-5F32-9809-DD089063E323','07B48961-9426-C1B0-FA86-14B539BF21B5',
        '9AA00718-03BB-51BF-89F4-28F0A9CB1B7E','1D0B9F2B-ACB0-F1D9-E9B9-1FCE98EB5AA2'].includes(strtoupper(trim(itemId)));
}
/** PHP: ft_shape_inventory_vitality (includes/functions/janus/encounter/ft3d_shape_core.php:1781) */
export function ftShapeInventoryVitality(inventory: unknown, field: string, env: ShapeEnv): unknown {
    if (inventory === null) return null;
    let value = env.deps.item_field(inventory, field);
    if (value === null && array(inventory).has(field)) {
        value = at(inventory, field); if (isArray(value)) value = at(value, 0);
    }
    return is_numeric(value) ? floatval(value) : null;
}
/** PHP: ft_shape_inventory_structure_state (includes/functions/janus/encounter/ft3d_shape_core.php:1568) */
export function ftShapeInventoryStructureState(inventory: unknown, cleared = false, env: ShapeEnv): string {
    if (Number(intval(at(inventory, 'inventory_deleted') ?? at(inventory, 'inventory', 'inventory_deleted') ?? 0)) === 1) return 'destroyed';
    return ftStructureState(ftShapeInventoryVitality(inventory, 'v_current', env), ftShapeInventoryVitality(inventory, 'v_max', env), cleared);
}
/** PHP: ft_shape_opening_inventory_id (includes/functions/janus/encounter/ft3d_shape_core.php:1819) */
export function ftShapeOpeningInventoryId(opening: unknown, env: ShapeEnv): string | null {
    const intent = at(opening, 'creation', 'connectionIntent');
    const id = at(intent, 'kind') === 'window-intent' ? at(intent, 'windowInventoryId') ?? at(intent, 'doorInventoryId') : at(intent, 'doorInventoryId');
    return typeof id === 'string' && env.deps.check_uuid(id) ? strtoupper(id) : null;
}
/** PHP: ft_shape_secret_door_bindings (includes/functions/janus/encounter/ft3d_shape_core.php:1612) */
export function ftShapeSecretDoorBindings(shape: unknown, env: ShapeEnv): unknown {
    const bindings = new PhpArray();
    for (const opening of values(at(shape, 'openings'))) {
        if (!isArray(opening) || !ftShapeIsRuntimeDoor(opening) || at(opening, 'creation', 'connectionIntent', 'secret') !== true) continue;
        const id = ftShapeOpeningInventoryId(opening, env);
        if (id !== null) bindings.set(trim(strval(at(opening, 'id'))), id);
    }
    return bindings;
}
/** PHP: ft_shape_concealed_opening_ids (includes/functions/janus/encounter/ft3d_shape_core.php:1691) */
export function ftShapeConcealedOpeningIds(shape: unknown, privateIds: unknown = null, env: ShapeEnv): unknown {
    const bindings = array(ftShapeSecretDoorBindings(shape, env));
    if (!bindings.size) return new PhpArray();
    const privateRows = privateIds ?? env.deps.ft_shape_private_door_inventory_ids(list([...bindings.values()]));
    const concealed = new PhpArray();
    for (const [opening, inventory] of bindings) if (privateRows === null || at(privateRows, strval(inventory)) !== null) concealed.set(opening, true);
    return concealed;
}
/** PHP: ft_shape_overlay_concealed_doors (includes/functions/janus/encounter/ft3d_shape_core.php:1714) */
export function ftShapeOverlayConcealedDoors(shape: unknown, env: ShapeEnv): unknown {
    const copy = phpCopy(array(shape)), bindings = array(ftShapeSecretDoorBindings(shape, env));
    if (!bindings.size) return copy;
    const concealed = array(ftShapeConcealedOpeningIds(shape, null, env));
    for (const [index, opening] of array(at(copy, 'openings'))) {
        if (!isArray(opening)) continue;
        const id = trim(strval(at(opening, 'id') ?? ''));
        if (!bindings.has(id)) continue;
        const row = array(opening); row.set('concealed', concealed.has(id));
        if (concealed.has(id)) { row.set('passable', false); row.set('transparent', false); row.delete('crossing'); }
        array(at(copy, 'openings')).set(index, row);
    }
    return copy;
}
/** PHP: ft_shape_overlay_door_states (includes/functions/janus/encounter/ft3d_shape_core.php:1534) */
export function ftShapeOverlayDoorStates(shape: unknown, states: unknown, encounter: unknown = [], env: ShapeEnv): unknown {
    const copy = phpCopy(array(shape)), inventories = env.deps.ft_shape_opening_vitality_rows(shape, encounter);
    for (const [index, opening] of isArray(at(copy, 'openings')) ? array(at(copy, 'openings')) : []) {
        if (!isArray(opening)) continue;
        const kind = ftShapeRuntimeStructureKind(opening); if (kind === '') continue;
        const id = trim(strval(at(opening, 'id'))), open = at(states, id, 'open') === true, cleared = at(states, id, 'cleared') === true;
        const inventory = at(inventories, ftShapeOpeningInventoryId(opening, env) ?? '');
        const state = ftShapeInventoryStructureState(inventory, cleared, env);
        const effect = ftStructureOpeningEffect(kind, state, open, ftShapeOpeningTransparentWhenShut(opening) || ftShapeDoorIdentitySeeThrough(opening, inventory, env));
        const row = array(opening); row.set('passable', at(effect, 'passable')); row.set('transparent', at(effect, 'transparent')); row.set('structureState', state);
        if (at(effect, 'crossing') !== null) row.set('crossing', at(effect, 'crossing'));
        array(at(copy, 'openings')).set(index, row);
    }
    return copy;
}
/** PHP: ft_door_inventory_for_opening (includes/functions/janus/encounter/ft3d_shape_core.php:1810) */
export function ftDoorInventoryForOpening(opening: unknown, env: ShapeEnv): unknown {
    const id = ftShapeOpeningInventoryId(opening, env); if (id === null) return null;
    const inventory = env.deps.get_inventory_arr(id); return isArray(inventory) && !empty(inventory) ? inventory : null;
}
/** PHP: ft_door_lock_type_for_opening (includes/functions/janus/encounter/ft3d_shape_core.php:1880) */
export function ftDoorLockTypeForOpening(opening: unknown, env: ShapeEnv): string | null {
    const inventory = ftDoorInventoryForOpening(opening, env); if (inventory === null) return null;
    const lock = env.deps.layer_value(inventory, 'lock_type');
    return typeof lock === 'string' && lock !== '' && env.deps.check_uuid(lock) ? lock : null;
}
/** PHP: ft_inventory_is_locked (includes/functions/janus/encounter/ft3d_shape_core.php:1914) */
export function ftInventoryIsLocked(inventory: unknown, env: ShapeEnv): boolean {
    if (empty(inventory)) return false;
    const id = catalogId('StatusEffect', 'locked', env); if (typeof id !== 'string' || id === '') return false;
    for (const direction of ['caster', 'target']) for (const row of values(env.deps.creature_statuses(inventory, direction))) {
        if ((env.deps.item_field(row, 'item_id') ?? at(row, 'item_id') ?? '') === id) return true;
    }
    return false;
}
/** PHP: ft_door_is_locked_for_opening (includes/functions/janus/encounter/ft3d_shape_core.php:2312) */
export function ftDoorIsLockedForOpening(opening: unknown, env: ShapeEnv): boolean {
    const inventory = ftDoorInventoryForOpening(opening, env); if (inventory === null) return false;
    if (ftInventoryIsLocked(inventory, env)) return true;
    const host = strval(env.deps.inventory_field(inventory, 'inventory_id') ?? '');
    for (const binding of values(env.deps.ft_inventory_lock_bindings_for_host(host))) {
        const lock = strval(env.deps.layer_value(binding, 'inventory_lock_binding_lock_id') ?? '');
        if (lock !== host && env.deps.check_uuid(lock) && ftInventoryIsLocked(env.deps.get_inventory_arr(lock) || [], env)) return true;
    }
    return false;
}
/** PHP: ft_aperture_side_of (includes/functions/janus/encounter/ft3d_shape_core.php:2001) */
export function ftApertureSideOf(shape: unknown, opening: unknown, x: unknown, y: unknown): string {
    const raw = at(opening, 'creation', 'rooms');
    const rooms = (isArray(raw) ? values(raw) : raw === null ? [] : [raw]).map(id => id !== null && !isArray(id) ? strval(id) : '').filter(id => id !== '');
    if (!rooms.length) return '';
    let known = false;
    for (const box of values(at(shape, 'boxes'))) {
        if (!isArray(box) || !rooms.includes(strval(at(box, 'id') ?? ''))) continue;
        known = true;
        if (num(x) >= num(at(box, 'x', 'min')) - EPS && num(x) <= num(at(box, 'x', 'max')) + EPS
            && num(y) >= num(at(box, 'y', 'min')) - EPS && num(y) <= num(at(box, 'y', 'max')) + EPS) return rooms.length === 1 ? 'inside' : strval(at(box, 'id'));
    }
    return known && rooms.length === 1 ? 'outside' : '';
}
/** PHP: ft_aperture_lock_operable_from (includes/functions/janus/encounter/ft3d_shape_core.php:2041) */
export function ftApertureLockOperableFrom(shape: unknown, opening: unknown, x: unknown, y: unknown): boolean {
    const allowed = values(ftApertureLatchSides(opening));
    if (!allowed.length || allowed.includes('inside') && allowed.includes('outside')) return true;
    const side = ftApertureSideOf(shape, opening, x, y); return side === '' || allowed.includes(side);
}
/** PHP: ft_shape_door_state_projection (includes/functions/janus/encounter/ft3d_shape_core.php:2329) */
export function ftShapeDoorStateProjection(shape: unknown, stored: unknown = []): unknown {
    const projection = new PhpArray();
    for (const opening of values(at(shape, 'openings'))) {
        if (!isArray(opening)) continue;
        const kind = ftShapeRuntimeStructureKind(opening); if (kind === '') continue;
        const id = trim(strval(at(opening, 'id')));
        const open = kind === 'window' ? at(stored, id, 'open') === true : at(opening, 'passable') === true && at(opening, 'transparent') === true;
        const row = record({ open, revision: max(0, intval(at(stored, id, 'revision') ?? 0)) });
        if (kind === 'window') {
            const state = strval(at(opening, 'structureState') ?? 'intact');
            row.set('windowPhase', ['cleared', 'destroyed'].includes(state) ? 'cleared' : state === 'damaged' ? 'broken' : open ? 'open' : 'closed');
        }
        projection.set(id, row);
    }
    return ksort(projection, SORT_STRING);
}
/** PHP: ft_shape_ambient_light_at (includes/functions/janus/encounter/ft3d_shape_core.php:2119) */
export function ftShapeAmbientLightAt(shape: unknown, base: string, x: unknown, y: unknown, relay = true, inside: unknown = null, env: ShapeEnv): string {
    const magical = '67F42B5B-E3D1-0818-0197-1F36B20FC630', black = '99D3E371-AC3F-7839-28E7-770FE50D24C1', dim = '61D1498F-9175-6906-4B26-8356C28B28C1';
    if (base === magical) return base;
    const boxes = at(shape, 'boxes'); if (!isArray(boxes) || empty(boxes)) return base;
    if (inside === null) for (const box of values(boxes)) {
        if (!isArray(box) || at(box, 'x', 'min') === null || at(box, 'y', 'min') === null) continue;
        if (num(x) >= num(at(box, 'x', 'min')) - EPS && num(x) <= num(at(box, 'x', 'max')) + EPS
            && num(y) >= num(at(box, 'y', 'min')) - EPS && num(y) <= num(at(box, 'y', 'max')) + EPS) { inside = box; break; }
    }
    if (inside === null || !env.deps.ft_shape_box_is_roofed(inside)) return base;
    if (ftLightRank(base) <= ftLightRank(black)) return black;
    const room = strval(at(inside, 'id') ?? '');
    for (const opening of values(at(shape, 'openings'))) {
        if (!isArray(opening) || !ftShapeOpeningPassesLight(opening)) continue;
        const raw = at(opening, 'creation', 'rooms'), rooms = isArray(raw) ? values(raw) : raw === null ? [] : [raw];
        if (rooms.length && !rooms.map(strval).includes(room) && !ftShapeOpeningOnBoxFace(opening, inside)) continue;
        if (!ftShapePointWithinApertureReach(opening, x, y)) continue;
        if (ftShapeOpeningFacing(shape, opening) === 'exterior') return dim;
        if (!relay) continue;
        const far = ftShapeApertureFarSidePoint(opening, inside); if (far === null) continue;
        let farBox: unknown = null;
        for (const box of values(boxes)) {
            if (!isArray(box) || identical(box, inside) || at(box, 'x', 'min') === null || at(box, 'y', 'min') === null) continue;
            if (num(at(far, 'x')) >= num(at(box, 'x', 'min')) - EPS && num(at(far, 'x')) <= num(at(box, 'x', 'max')) + EPS
                && num(at(far, 'y')) >= num(at(box, 'y', 'min')) - EPS && num(at(far, 'y')) <= num(at(box, 'y', 'max')) + EPS
                && num(at(box, 'floor')) < num(at(opening, 'z', 'max')) - EPS && num(at(box, 'ceiling')) > num(at(opening, 'z', 'min')) + EPS) { farBox = box; break; }
        }
        const farLight = farBox !== null ? ftShapeAmbientLightAt(shape, base, at(far, 'x'), at(far, 'y'), false, farBox, env) : base;
        if (ftLightRank(farLight) >= ftLightRank(dim)) return dim;
    }
    return black;
}
/** PHP: ft_placement_ground_z (includes/functions/janus/encounter/ft3d_shape_core.php:2417) */
export function ftPlacementGroundZ(encounterId: string | null, obj: unknown, shape: unknown = null, x: unknown = 0.0, y: unknown = 0.0, env: ShapeEnv): unknown {
    if (!env.deps.ft_encounter_id_is_depth_authored(encounterId)) return f(num(env.deps.get_object_height_feet(obj)) / 2);
    return isArray(shape) ? env.deps.ft_shape_floor_at(shape, floatval(x), floatval(y)) ?? f(0.0) : f(0.0);
}
/** PHP: ft_row_encounter_id (includes/functions/janus/encounter/ft3d_shape_core.php:2479) */
export function ftRowEncounterId(row: unknown, env: ShapeEnv): string | null {
    if (!isArray(row)) return null;
    const id = at(row, 'encounter_creatures', 'encounter_id') ?? env.deps.inventory_field(row, 'possess_id') ?? at(row, 'inventory', 'possess_id');
    return typeof id === 'string' && id !== '' ? id : null;
}
/** PHP: ft_landed_z (includes/functions/janus/encounter/ft3d_shape_core.php:2442) */
export function ftLandedZ(obj: unknown, surface: unknown, physicsHeight: unknown, env: ShapeEnv): unknown {
    return env.deps.ft_encounter_id_is_depth_authored(ftRowEncounterId(obj, env)) ? floatval(surface) : f(num(surface) + num(physicsHeight) / 2);
}
/** PHP: ft_encounter_shape_or_derived (includes/functions/janus/encounter/ft3d_shape_core.php:2499) */
export function ftEncounterShapeOrDerived(encounter: unknown, env: ShapeEnv): unknown {
    const stored = env.deps.ft_encounter_shape(encounter); if (stored !== null) return record({ shape: stored, source: 'stored' });
    let width = num(env.deps.encounter_field(encounter, 'encounter_max_x') ?? 0), depth = num(env.deps.encounter_field(encounter, 'encounter_max_y') ?? 0);
    width = width > 0 ? width : 100.0; depth = depth > 0 ? depth : 100.0;
    const ceiling = env.deps.encounter_field(encounter, 'encounter_ceiling'), maxZ = num(env.deps.encounter_field(encounter, 'encounter_max_z') ?? 0);
    const top = is_numeric(ceiling) && num(ceiling) > 0 ? num(ceiling) : maxZ > 0 ? maxZ : 1000.0;
    const interior = strval(env.deps.encounter_field(encounter, 'encounter_interior_type') ?? '');
    const box = record({ id: 'main', x: record({ min: f(-Math.floor(width / 2)), max: f(Math.ceil(width / 2)) }),
        y: record({ min: f(-Math.floor(depth / 2)), max: f(Math.ceil(depth / 2)) }), floor: 0, ceiling: f(top) });
    const shape = record({ v: 1, unit: 'ft', boxes: list([box]), openings: list([]) });
    if (interior !== '') box.set('interior', interior); else shape.set('derivedExteriorFallback', true);
    return record({ shape, source: 'derived-one-box' });
}
/** PHP: ft_shape_sight_blocked (includes/functions/janus/encounter/ft3d_shape_core.php:2603) */
export function ftShapeSightBlocked(shape: unknown, x1: unknown, y1: unknown, x2: unknown, y2: unknown, z: unknown = 2.5, env: PortEnv<object>): boolean {
    return ftShapeSegmentHit(shape, x1, y1, x2, y2, z, 'sight', false, env) !== null;
}
/** PHP: ft_shape_sight_blocks_line (includes/functions/janus/encounter/ft3d_shape_core.php:2648) */
export function ftShapeSightBlocksLine(shape: unknown, x1: unknown, y1: unknown, x2: unknown, y2: unknown, z: unknown = 2.5, env: PortEnv<object>): boolean {
    if (empty(at(shape, 'boxes')) || ftShapeIsDerivedExteriorFallback(shape)) return false;
    const both = ftShapeInsideUnion(shape, x1, y1, env) && ftShapeInsideUnion(shape, x2, y2, env);
    return ftShapeSegmentHit(shape, x1, y1, x2, y2, z, 'sight', both, env) !== null;
}
/** PHP: ft_viewer_see_through_walls_ft (includes/functions/janus/encounter/ft3d_shape_core.php:2827) */
export function ftViewerSeeThroughWallsFt(viewer: unknown, env: ShapeEnv): unknown {
    const layer = 'see_through_walls_max_thickness_ft'; let root = at(viewer, layer) ?? 0;
    if (isArray(root)) root = at(root, 0) ?? 0;
    return max(0, intval(is_numeric(root) ? root : 0), intval(env.deps.layer_value(viewer, layer) ?? 0));
}
/** PHP: ft_aoe_arc_max_range_ft (includes/functions/janus/encounter/ft3d_shape_core.php:2980) */
export function ftAoeArcMaxRangeFt(item: unknown, env: ShapeEnv): unknown {
    if (at(item, 'computed', 'throw_object') !== null || at(item, 'computed', 'throw_mode') !== null) return null;
    const range = env.deps.layer_value(item, 'range');
    if (is_numeric(range)) return num(range) > 0 ? floatval(range) : null;
    if (typeof range !== 'string' || range === '') return null;
    const distance = at(catalogById('RangeType', range, env), 'x_distance');
    return is_numeric(distance) && num(distance) > 0 ? floatval(distance) : null;
}
/** PHP: ft_aoe_arc_test_segments (includes/functions/janus/encounter/ft3d_shape_core.php:3045) */
export function ftAoeArcTestSegments(points: unknown): unknown {
    const out: unknown[] = [], rows = values(points);
    for (let i = 1; i < rows.length; i++) {
        const a = rows[i - 1], b = rows[i], previous = i >= 2 ? rows[i - 2] : null;
        const start = previous === null ? phpCopy(a) : record(Object.fromEntries(['x','y','z'].map(axis => [axis,
            f(num(at(a, axis)) - (num(at(a, axis)) - num(at(previous, axis))) * .05)])));
        out.push(list([start, phpCopy(b)]));
    }
    return list(out);
}
/** PHP: ft_shape_box_structurally_interior (includes/functions/janus/encounter/ft3d_shape_core.php:3214) */
export function ftShapeBoxStructurallyInterior(box: unknown): boolean {
    const creation = at(box, 'creation'); if (!isArray(creation)) return false;
    const preset = strtolower(trim(strval(at(box, 'presetId') ?? at(creation, 'presetId') ?? '')));
    if (preset === 'exterior-open') return false;
    if (preset !== '' && preset !== 'legacy-structural') return true;
    return (at(box, 'environment') ?? at(creation, 'environment')) === 'interior';
}
/** PHP: ft_shape_light_fixtures (includes/functions/janus/encounter/ft3d_shape_core.php:3232) */
export function ftShapeLightFixtures(shape: unknown): unknown {
    return list(shapeLightFixtures(native(shape)).map((row: {id: string; boxId: string; wall: string; x: number; y: number; z: number}) =>
        record({ id: row.id, boxId: row.boxId, wall: row.wall, x: f(row.x), y: f(row.y), z: f(row.z) })));
}
/** PHP: ft_shape_segment_intersects_cover_volume (includes/functions/janus/encounter/ft3d_shape_core.php:735) */
export function ftShapeSegmentIntersectsCoverVolume(source: unknown, target: unknown, volume: unknown): boolean {
    const callback = at(volume, 'intersectsSegment'); if (typeof callback === 'function') return callback(source, target) as boolean;
    const sx = num(at(source, 'x')), sy = num(at(source, 'y')), sz = num(at(source, 'z'));
    const tx = num(at(target, 'x')), ty = num(at(target, 'y')), tz = num(at(target, 'z'));
    const zInterval = ftShapeSegmentIntervalForAxis(sz, tz - sz, at(volume, 'zMin'), at(volume, 'zMax')); if (zInterval === null) return false;
    let horizontal: unknown = null;
    if (at(volume, 'kind') === 'circle') {
        const dx = tx - sx, dy = ty - sy, ox = sx - num(at(volume, 'x')), oy = sy - num(at(volume, 'y'));
        const a = dx * dx + dy * dy; if (a < EPS) return false;
        const b = 2 * (ox * dx + oy * dy), c = ox * ox + oy * oy - num(at(volume, 'r')) * num(at(volume, 'r'));
        const discriminant = b * b - 4 * a * c; if (discriminant < -EPS) return false;
        const root = Math.sqrt(Math.max(0.0, discriminant));
        horizontal = list([f((-b - root) / (2 * a)), f((-b + root) / (2 * a))]);
    } else if (at(volume, 'kind') === 'oblong') {
        const cos = Math.cos(-num(at(volume, 'facing') ?? 0.0)), sin = Math.sin(-num(at(volume, 'facing') ?? 0.0));
        const local = (x: number, y: number): [number, number] => {
            const xx = x - num(at(volume, 'x')), yy = y - num(at(volume, 'y')); return [xx * cos - yy * sin, xx * sin + yy * cos];
        };
        const [ax, ay] = local(sx, sy), [bx, by] = local(tx, ty);
        horizontal = ftShapeIntersectIntervals(list([
            ftShapeSegmentIntervalForAxis(ax, bx - ax, -num(at(volume, 'halfW')), at(volume, 'halfW')),
            ftShapeSegmentIntervalForAxis(ay, by - ay, -num(at(volume, 'halfD')), at(volume, 'halfD')),
        ]));
    }
    return horizontal !== null && ftShapeIntersectIntervals(list([horizontal, zInterval, list([f(EPS), f(1 - EPS)])])) !== null;
}
/** PHP: ft_shape_directional_cover_samples (includes/functions/janus/encounter/ft3d_shape_core.php:786) */
export function ftShapeDirectionalCoverSamples(source: unknown, volume: unknown, hunkered = false): unknown {
    if (at(volume, 'coverSamplePoints') !== null) return phpCopy(at(volume, 'coverSamplePoints'));
    const dx = num(at(volume, 'x')) - num(at(source, 'x')), dy = num(at(volume, 'y')) - num(at(source, 'y')), length = Math.hypot(dx, dy);
    const px = length > EPS ? -dy / length : 1.0, py = length > EPS ? dx / length : 0.0;
    const lateral = (at(volume, 'kind') === 'oblong' ? Math.max(num(at(volume, 'halfW')), num(at(volume, 'halfD'))) : num(at(volume, 'r'))) * .72;
    const zmin = num(at(volume, 'zMin')), height = Math.max(EPS, num(at(volume, 'zMax')) - zmin), exposed = height * (hunkered ? .62 : 1.0);
    const out: unknown[] = [];
    for (const level of [.2, .5, .8]) for (const offset of [-lateral, 0.0, lateral]) out.push(record({
        x: f(num(at(volume, 'x')) + px * offset), y: f(num(at(volume, 'y')) + py * offset), z: f(zmin + exposed * level),
    }));
    return list(out);
}
/** PHP: ft_shape_flanking_angle_deg (includes/functions/janus/encounter/ft3d_shape_core.php:822) */
export function ftShapeFlankingAngleDeg(caster: unknown, target: unknown, ally: unknown): unknown {
    const cx = num(at(caster, 'x')) - num(at(target, 'x')), cy = num(at(caster, 'y')) - num(at(target, 'y'));
    const ax = num(at(ally, 'x')) - num(at(target, 'x')), ay = num(at(ally, 'y')) - num(at(target, 'y'));
    const cl = Math.hypot(cx, cy), al = Math.hypot(ax, ay); if (cl <= EPS || al <= EPS) return null;
    const cos = Math.max(-1.0, Math.min(1.0, (cx * ax + cy * ay) / (cl * al)));
    return f(Math.acos(cos) / Math.PI * 180);
}
/** PHP: ft_shape_directional_cover_between (includes/functions/janus/encounter/ft3d_shape_core.php:837) */
export function ftShapeDirectionalCoverBetween(shape: unknown, source: unknown, volume: unknown, occluders: unknown = [],
    hunkered = false, partialDelta: unknown = 2, heavyDelta: unknown = 4, env: PortEnv<object>): unknown {
    const samples = values(ftShapeDirectionalCoverSamples(source, volume, hunkered));
    let structural = 0, volumes = 0, count = 0;
    for (const sample of samples) {
        let blocked = ftShapeSegmentHit3d(shape, source, sample, 'sight', true, env) !== null;
        if (blocked) structural++;
        else for (const occluder of values(occluders)) if (ftShapeSegmentIntersectsCoverVolume(source, sample, occluder)) { volumes++; blocked = true; break; }
        if (blocked) count++;
    }
    let tier = 'none'; if (count >= 2) tier = 'partial'; if (count >= 6) tier = 'heavy'; if (count === samples.length) tier = 'blocked';
    return record({ tier, blockedSamples: count, totalSamples: samples.length,
        effectiveDefenseDelta: tier === 'partial' ? intval(partialDelta) : tier === 'heavy' ? intval(heavyDelta) : 0,
        hunkerApplied: hunkered && count > 0, structuralBlockedSamples: structural, volumeBlockedSamples: volumes });
}
/** PHP: ft_shape_cover_between (includes/functions/janus/encounter/ft3d_shape_core.php:899) */
export function ftShapeCoverBetween(shape: unknown, ax: unknown, ay: unknown, tx: unknown, ty: unknown,
    z: unknown = 3.0, half: unknown = 2.5, env: PortEnv<object>): unknown {
    const inset = num(half) * .9, x = num(tx), y = num(ty);
    const corners = [[x - inset, y - inset], [x + inset, y - inset], [x - inset, y + inset], [x + inset, y + inset]];
    const blocked = corners.map(([cx, cy]) => ftShapeSegmentHit(shape, ax, ay, cx, cy, z, 'sight', false, env) !== null);
    const count = blocked.filter(Boolean).length, center = ftShapeSegmentHit(shape, ax, ay, tx, ty, z, 'sight', false, env) !== null;
    let tier = 'none'; if (count >= 1) tier = 'half'; if (count >= 3) tier = 'three-quarters'; if (count === 4 && center) tier = 'total';
    return record({ tier, blockedCount: count, centerBlocked: center, cornerBlocked: list(blocked) });
}
/** PHP: ft_shape_aoe_cast_path_blocked (includes/functions/janus/encounter/ft3d_shape_core.php:2902) */
export function ftShapeAoeCastPathBlocked(shape: unknown, caster: unknown, x: unknown, y: unknown, z: unknown, env: ShapeEnv): boolean {
    if (shape === null || !isArray(caster) || x === null || y === null || z === null) return false;
    const center = env.deps.ft_shape_object_center(caster), cx = at(center, 0), cy = at(center, 1);
    if (!ftShapeInsideUnion(shape, cx, cy, env) || !ftShapeInsideUnion(shape, x, y, env)) return false;
    const source = record({ x: cx, y: cy, z: env.deps.ft_shape_ray_z(shape, caster, cx, cy) });
    return ftShapeSightSegmentHit3d(shape, source, record({ x: floatval(x), y: floatval(y), z: floatval(z) }), true, env) !== null;
}
/** PHP: ft_shape_aoe_arc_path_blocked (includes/functions/janus/encounter/ft3d_shape_core.php:3004) */
export function ftShapeAoeArcPathBlocked(shape: unknown, caster: unknown, x: unknown, y: unknown, z: unknown, range: unknown, env: ShapeEnv): boolean {
    if (shape === null || !isArray(caster) || x === null || y === null || z === null) return false;
    const center = env.deps.ft_shape_object_center(caster), cx = at(center, 0), cy = at(center, 1);
    if (!ftShapeInsideUnion(shape, cx, cy, env) || !ftShapeInsideUnion(shape, x, y, env)) return false;
    const emitter = record({ x: cx, y: cy, z: env.deps.ft_shape_ray_z(shape, caster, cx, cy) });
    const landing = record({ x: floatval(x), y: floatval(y), z: floatval(z) });
    for (const pair of values(ftAoeArcTestSegments(ftAoeArcSamples(emitter, landing, range))))
        if (ftShapeSightSegmentHit3d(shape, at(pair, 0), at(pair, 1), true, env) !== null) return true;
    return false;
}
/** PHP: ft_shape_floor_at (includes/functions/janus/encounter/ft3d_shape_core.php:320) */
export function ftShapeFloorAt(shape: unknown, x: unknown, y: unknown): unknown {
    let boxes = values(ftShapeContainingBoxes(shape, x, y)); if (!boxes.length) return null;
    const structural = boxes.filter(box => !ftShapeBoxIsNavigationOpenGround(box)); if (structural.length) boxes = structural;
    let best: number | null = null; for (const box of boxes) { const floor = num(at(box, 'floor')); if (best === null || floor < best) best = floor; }
    return best === null ? null : f(best);
}
/** PHP: ft_shape_sight_wall_thickness_ft (includes/functions/janus/encounter/ft3d_shape_core.php:2719) */
export function ftShapeSightWallThicknessFt(shape: unknown, x1: unknown, y1: unknown, x2: unknown, y2: unknown, z: unknown, limit: unknown, env: PortEnv<object>): unknown {
    if (empty(at(shape, 'boxes')) || ftShapeIsDerivedExteriorFallback(shape)) return f(0.0);
    const ax = num(x1), ay = num(y1), bx = num(x2), by = num(y2), zz = num(z);
    const length = Math.sqrt((bx - ax) * (bx - ax) + (by - ay) * (by - ay)); if (length < 1e-6) return f(0.0);
    const ux = (bx - ax) / length, uy = (by - ay) / length, solid: unknown[] = [];
    for (const wall of values(at(shape, 'sceneWalls'))) {
        if (!isArray(wall) || !['x','y'].includes(strval(at(wall, 'axis') ?? ''))) continue;
        const plane = num(at(wall, 'at') ?? 0), half = Math.max(.05, num(at(wall, 'thicknessFt') ?? .5)) / 2;
        const low = num(at(wall, 'span', 'min') ?? 0), high = num(at(wall, 'span', 'max') ?? 0);
        const floor = num(at(wall, 'z', 'min') ?? 0), ceiling = num(at(wall, 'z', 'max') ?? 0);
        if (!(high > low) || !(ceiling > floor) || zz < floor || zz > ceiling) continue;
        const [ac0, au, al0, alu] = at(wall, 'axis') === 'x' ? [ax, ux, ay, uy] : [ay, uy, ax, ux];
        const body = ftSightIntersect(ftSightRayRange(ac0, au, plane - half, plane + half, length), ftSightRayRange(al0, alu, low, high, length)); if (body === null) continue;
        let pieces: unknown[] = [body];
        for (const opening of values(at(wall, 'openings'))) {
            if (!isArray(opening) || zz < num(at(opening, 'z', 'min') ?? floor) || zz > num(at(opening, 'z', 'max') ?? ceiling)) continue;
            const hole = ftSightRayRange(al0, alu, at(opening, 'span', 'min') ?? 0, at(opening, 'span', 'max') ?? 0, length); if (hole === null) continue;
            const next: unknown[] = [];
            for (const piece of pieces) {
                const cut = ftSightIntersect(piece, hole); if (cut === null) { next.push(piece); continue; }
                for (const range of [list([at(piece, 0), at(cut, 0)]), list([at(cut, 1), at(piece, 1)])]) if (num(at(range, 1)) > num(at(range, 0))) next.push(range);
            }
            pieces = next;
        }
        solid.push(...pieces);
    }
    const parts: unknown[] = [];
    for (const box of values(at(shape, 'boxes'))) {
        const interval = ftSightIntersect(ftSightRayRange(ax, ux, at(box, 'x', 'min'), at(box, 'x', 'max'), length), ftSightRayRange(ay, uy, at(box, 'y', 'min'), at(box, 'y', 'max'), length));
        if (interval !== null) parts.push(interval);
    }
    const union = values(ftSightMerge(list(parts))); for (let i = 1; i < union.length; i++) solid.push(list([at(union[i - 1], 1), at(union[i], 0)]));
    const merged = values(ftSightMerge(list(solid))); let total = 0.0;
    for (const interval of merged) total += num(at(interval, 1)) - num(at(interval, 0)); if (total > num(limit)) return f(total);
    const both = ftShapeInsideUnion(shape, ax, ay, env) && ftShapeInsideUnion(shape, bx, by, env); let start = 0.0;
    for (let guard = 0; guard < 256; guard++) {
        const hit = ftShapeSegmentHit(shape, ax + ux * start, ay + uy * start, bx, by, zz, 'sight', both, env); if (hit === null) return f(total);
        const distance = start + num(at(hit, 't')) * (length - start);
        const onEdge = merged.some(interval => distance >= num(at(interval, 0)) - .02 && distance <= num(at(interval, 1)) + .02);
        if (!onEdge) { total += .5; if (total > num(limit)) return f(total); }
        start = distance + .01; if (start >= length) return f(total);
    }
    return f(Infinity);
}
/** PHP: ft_shape_sight_blocks_line_for_viewer (includes/functions/janus/encounter/ft3d_shape_core.php:2837) */
export function ftShapeSightBlocksLineForViewer(shape: unknown, viewer: unknown, x1: unknown, y1: unknown, x2: unknown, y2: unknown, z: unknown = 2.5, env: ShapeEnv): boolean {
    if (!ftShapeSightBlocksLine(shape, x1, y1, x2, y2, z, env)) return false;
    const limit = ftViewerSeeThroughWallsFt(viewer, env);
    return num(limit) <= 0 || num(ftShapeSightWallThicknessFt(shape, x1, y1, x2, y2, z, limit, env)) > num(limit);
}

/** PHP: ft_shape_overlay_wall_breaches (includes/functions/janus/encounter/ft3d_shape_core.php:1289) */
export function ftShapeOverlayWallBreaches(shapeInput: unknown, breachedInput: unknown, env: PortEnv<WallBreachDeps>): unknown {
    const shape = array(phpCopy(shapeInput)); if (values(breachedInput).length === 0) return shape;
    const existing = new Set<string>(), runs = new Set<string>(), refused = new Set<string>();
    for (const opening of values(at(shape, 'openings'))) {
        if (isArray(opening) && at(opening, 'id') !== null) existing.add(strval(at(opening, 'id')));
        const run = at(opening, 'creation', 'wallBreach', 'runKey'); if (isArray(opening) && typeof run === 'string') runs.add(run);
    }
    for (const refusal of values(at(shape, 'wallBreachRefusals'))) { const key = at(refusal, 'runKey'); if (isArray(refusal) && typeof key === 'string') refused.add(key); }
    let capacity = 128 - values(at(shape, 'openings')).length, dropped = 0;
    const sceneIndex = new Map<string, string | number | bigint>();
    for (const [index, wall] of array(at(shape, 'sceneWalls') ?? list([]))) { const id = at(wall, 'id'); if (isArray(wall) && typeof id === 'string') sceneIndex.set(id, index); }
    const sceneRuns = values(breachedInput).filter(run => isArray(run) && typeof at(run, 'sceneWallId') === 'string');
    const breached = values(breachedInput).filter(run => !isArray(run) || typeof at(run, 'sceneWallId') !== 'string');
    for (const run of sceneRuns) {
        const index = sceneIndex.get(strval(at(run, 'sceneWallId'))); if (index === undefined) continue;
        const wall = at(shape, 'sceneWalls', index), key = strval(at(run, 'key') ?? ''), openings = isArray(at(wall, 'openings')) ? array(at(wall, 'openings')) : list([]);
        if (values(openings).some(o => at(o, 'creation', 'wallBreach', 'runKey') === key)) continue;
        const lo = Math.max(num(at(wall, 'span', 'min')), num(at(run, 'span', 'min') ?? 0)), hi = Math.min(num(at(wall, 'span', 'max')), num(at(run, 'span', 'max') ?? 0));
        if (!(hi > lo)) continue; if (openings.size >= 64) { dropped++; continue; }
        const zlo = Math.max(num(at(wall, 'z', 'min')), num(at(run, 'z', 'min') ?? -Infinity)), zhi = Math.min(num(at(wall, 'z', 'max')), num(at(run, 'z', 'max') ?? Infinity));
        if (!(zhi > zlo)) continue;
        openings.append(record({span: record({min: f(lo), max: f(hi)}), z: record({min: f(zlo), max: f(zhi)}), creation: record({wallBreach: record({runKey: key, inventoryId: strval(at(run, 'inventoryId') ?? '')})})}));
        put(shape, ['sceneWalls', index, 'openings'], openings);
    }
    for (const run of breached) {
        const stale = values(at(shape, 'openings')).some(authored => {
            if (!isArray(authored) || at(authored, 'creation', 'wallBreach') !== null) return false;
            if (strval(at(authored, 'plane', 'axis') ?? '') !== strval(at(run, 'axis')) || Math.abs(num(at(authored, 'plane', 'at') ?? Infinity) - num(at(run, 'at'))) > .001) return false;
            return num(at(authored, 'span', 'min') ?? 0) < num(at(run, 'span', 'max')) && num(at(authored, 'span', 'max') ?? 0) > num(at(run, 'span', 'min'))
                && num(at(authored, 'z', 'min') ?? 0) < num(at(run, 'z', 'max')) && num(at(authored, 'z', 'max') ?? 0) > num(at(run, 'z', 'min'));
        });
        if (stale) continue;
        const key = strval(at(run, 'key') ?? ''), id = 'wall-breach-' + sha256(key).slice(0, 16);
        if (existing.has(id) || runs.has(key) || refused.has(key)) continue; existing.add(id);
        const opening = record({id, plane: record({axis: strval(at(run, 'axis') ?? ''), at: f(num(at(run, 'at') ?? 0))}),
            span: record({min: f(num(at(run, 'span', 'min') ?? 0)), max: f(num(at(run, 'span', 'max') ?? 0))}),
            z: record({min: f(num(at(run, 'z', 'min') ?? 0)), max: f(num(at(run, 'z', 'max') ?? 0))}), passable: true, transparent: true,
            creation: record({wallBreach: record({runKey: key, inventoryId: strval(at(run, 'inventoryId') ?? '')})})});
        const candidate = shape.copy(phpCopy); candidate.set('openings', list([opening])); candidate.delete('wallBreachOverflow'); candidate.delete('wallBreachRefusals');
        if (!env.deps.ft_shape_validate(candidate)) {
            if (at(shape, 'wallBreachRefusals') === null) shape.set('wallBreachRefusals', list([]));
            array(at(shape, 'wallBreachRefusals')).append(record({runKey: key, inventoryId: strval(at(run, 'inventoryId') ?? ''), reason: 'not-representable-on-shape-v1-box-face'})); refused.add(key); continue;
        }
        if (capacity <= 0) { dropped++; continue; }
        if (at(shape, 'openings') === null) shape.set('openings', list([]));
        array(at(shape, 'openings')).append(opening); existing.add(id); runs.add(key); capacity--;
    }
    if (dropped > 0) shape.set('wallBreachOverflow', dropped);
    return shape;
}
interface WallBreachDeps { ft_shape_validate(shape: unknown): boolean; }
